<script setup>

import {computed, onMounted, reactive, ref} from "vue";
import FrostedBar from "../layout/pageHeadings/frostedBar.vue";

const props = defineProps({
    pageType: {
        type: String,
        required: false,
        default: null,
    },
    flowieHandoff: {
        type: Object,
        required: false,
        default: null,
    },
    flowieReference: {
        type: String,
        required: false,
        default: '',
    },
})

const selectableTypes = ['webpage', 'landingpage', 'customerarea'];

const selectedType = ref(props.pageType);
const typeIsLocked = computed(() => props.pageType !== null);

const page = reactive({
    title: '',
    password: '',
    template_id: null,
    public: true,
    ai_page: false,
});

const savingState = ref('');

const useTemplateOptions = [
    {
        label: _mft('pagebuilderTemplate:useEmpty'),
        name: false,
    },
    {
        label: _mft('pagebuilderTemplate:useTemplate'),
        name: true,
    },
]
const useTemplate = ref(false)

const setUseTemplate = (e) => {
    if (e.name === false) {
        page.template_id = null;
        useTemplate.value = false
        return
    }
    useTemplate.value = true
}

const selectTemplate = (template) => {
    page.template_id = template.id;
}

const selectType = (type) => {
    selectedType.value = type;
    page.template_id = null;
    if (type === 'app_page') {
        useTemplate.value = false;
    }
}

const createPage = async () => {
    if (savingState.value === 'waiting' || ! selectedType.value) {
        return
    }
    savingState.value = 'waiting'

    try {
        const payload = props.flowieReference
            ? { ...page, flowie_handoff: props.flowieReference }
            : page
        const res = await axios.post(route('pages.store', selectedType.value), payload)
        if (res.data.success) {
            window.location.href = res.data.redirect_url
        } else {
            savingState.value = 'error'
        }
    } catch (e) {
        savingState.value = 'error'
    }
}

const pageValidates = computed(() => {
    return selectedType.value !== null && page.title.length > 0;
})

onMounted(() => {
    if (selectedType.value === 'app_page') {
        useTemplate.value = false
    }

    const params = new URLSearchParams(window.location.search);
    if (params.get('title')) {
        page.title = params.get('title');
    }
    if (params.get('ai_page') === '1') {
        page.ai_page = true;
        useTemplate.value = false;
        page.template_id = null;
    }

    // Context forwarded from Flowie. Prefilled for the user to confirm —
    // nothing is created until they submit the form themselves.
    if (props.flowieHandoff) {
        if (props.flowieHandoff.page_title) {
            page.title = props.flowieHandoff.page_title;
        }
        if (props.flowieHandoff.page_type && !typeIsLocked.value) {
            selectType(props.flowieHandoff.page_type);
        }
    }
})

</script>
<template>
    <div class="md:sticky top-2 z-topbar">
        <frosted-bar>
            <div class="flex items-center justify-between space-x-4 f">
                <h1>{{ page.title === '' ? _mft('page:createPage') : page.title }}</h1>

                <div class="flex items-center space-x-4">
                    <story-lane-modal :module="'pages'"></story-lane-modal>

                    <button-base
                        :style-type="pageValidates ? 'success' : 'disabled'"
                        :disabled="! pageValidates"
                        @click="createPage()"
                        :state="savingState"
                    >
                        {{ _mft('page:createPage') }}
                    </button-base>
                </div>
            </div>
        </frosted-bar>
    </div>

    <div class="mt-12 xl:mt-24 mb-12 max-w-3xl mx-auto">
        <div v-if="props.pageType !== 'app_page'" class="mb-12 max-w-2xl mx-auto">
            <p class="text-sm font-medium text-gray-700 mb-3">{{ _mft('page:create.selectType') }}</p>
            <div class="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <button-base
                    v-for="type in selectableTypes"
                    :key="type"
                    @click="selectType(type)"
                    :style-type="selectedType === type ? 'tinted' : 'secondary'"
                >
                    <div class="text-sm font-semibold text-gray-800">{{ _mft('page:' + type + '.domainNameSingular') }}</div>
                    <div class="text-xs text-gray-500 mt-1">{{ _mft('page:create.type.' + type + '.description') }}</div>
                </button-base>
            </div>
        </div>

        <template v-if="selectedType">
            <input-text
                name="title"
                label="page:pageTitle"
                v-model:content="page.title"
                required
                autofocus
            ></input-text>

            <input-text
                name="password"
                label="page:password"
                v-model:content="page.password"
            ></input-text>

            <toggle-switch
                class="mt-4"
                name="public"
                label="page:state.published"
                v-model:content="page.public"
            ></toggle-switch>

            <div v-if="selectedType !== 'app_page'" class="mt-8 max-w-2xl mx-auto">
                <p class="text-sm font-medium text-gray-700 mb-3">{{ _mft('page:editorType') }}</p>
                <div class="grid grid-cols-2 gap-4">
                    <button-base
                        @click="page.ai_page = false"
                        :style-type="!page.ai_page ? 'tinted' : 'secondary'"
                    >
                        <div class="text-sm font-semibold text-gray-800">{{ _mft('page:editorType.blockBuilder') }}</div>
                        <div class="text-xs text-gray-500 mt-1">{{ _mft('page:editorType.blockBuilder.description') }}</div>
                    </button-base>
                    <button-base
                        @click="page.ai_page = true; useTemplate = false; page.template_id = null;"
                        :style-type="page.ai_page ? 'tinted' : 'secondary'"
                    >
                        <div class="text-sm font-semibold text-gray-800">{{ _mft('page:editorType.aiEditor') }}</div>
                        <div class="text-xs text-gray-500 mt-1">{{ _mft('page:editorType.aiEditor.description') }}</div>
                    </button-base>
                </div>
            </div>

            <button-bar
                v-if="selectedType !== 'app_page' && !page.ai_page"
                :options="useTemplateOptions"
                @select="setUseTemplate"
                class="mt-8 max-w-2xl mx-auto"
            />
        </template>
    </div>

    <templates-list
        v-if="selectedType && useTemplate && !page.ai_page"
        :show-empty-default="false"
        :read-only="true"
        :selected-template-id="page.template_id"
        :preset-category="selectedType"
        @selectTemplate="selectTemplate"
    ></templates-list>

</template>
